/**
 * @fileOverview The materials walkthrough. Pure data.
 * See docs/view-help.md and docs/view-help/materials.md.
 */

import { ENTITY_ICONS } from "@/services/entityIcons"
import { PERMISSIONS } from "@/services/permission"
import { hasPermissionsOfAll } from "@/services/permissionService"
import { isSupplierStrict } from "@/services/supplierMode"
import { stepsOn } from "@/services/viewHelp/define"
import type { ViewHelpTour } from "@/services/viewHelp/types"

/** Reading the material catalogue — the list and a material's page need it (`router/routes/material.js`). */
const VIEW = [PERMISSIONS.VIEW_MATERIAL_GENERAL]

/** Entering or changing one — the create button and Speichern. */
const EDIT = [PERMISSIONS.EDIT_MATERIAL_GENERAL]

/** The purchase price is a right of its own: without it the column and the Einkauf fields are gone. */
const VIEW_PRICE = [PERMISSIONS.VIEW_MATERIAL_PURCHASEPRICE]

/**
 * Once the workspace's setup is done, the prices are suppliers' and live in an Einkauf tab of their own; before,
 * they are a group of the Allgemein tab (docs/suppliers.md, "Setup and strict mode"). Each state has its steps.
 */
const strict = () => isSupplierStrict()
const legacy = () => !isSupplierStrict()

/** "Preis hinzufügen": changing the material's prices, and picking a supplier, which needs seeing them. */
const mayAddSupplier = () =>
  isSupplierStrict() &&
  hasPermissionsOfAll([PERMISSIONS.EDIT_MATERIAL_PURCHASEPRICE, PERMISSIONS.VIEW_SUPPLIER_GENERAL]).value

/** The hint under the suppliers counts bookings only for somebody who may re-price a project's materials. */
const REPRICE = [
  PERMISSIONS.EDIT_ASSIGNED_PROJECT_MATERIAL_PURCHASEPRICE,
  PERMISSIONS.EDIT_FOREIGN_PROJECT_MATERIAL_PURCHASEPRICE,
]

/** The sample material every step of its page is read on — the world's Beton C25/30. */
const MATERIAL = { materialId: "mat-beton" }

const onOverview = stepsOn("MaterialOverview", { permissions: VIEW })
const onDetail = stepsOn("MaterialDetail", { permissions: VIEW, at: { params: MATERIAL } })
// Two materials side by side (the add-on's Vergleich, a material's "Aktionen → Vergleichen"): Kies 0/32 and
// Bewehrungsstahl, both by the tonne; Huber and Krämer sell both, Seefeld only Kies.
const onComparison = stepsOn("MaterialComparison", {
  navEntry: "nav-material-comparison",
  at: { query: { ids: "mat-kies,mat-stahl" } },
})

// The related views: where a material takes effect outside the catalogue — the customer's own selling
// price, the packet that bundles it, the project registers that carry and book it, and the running
// post-calculation that prices what was booked. The pointer walks there (`services/viewHelp/ways.ts`); `at` says
// where to load the frame if that walk ever fails.
const onCustomer = stepsOn("CustomerDetail", {
  permissions: [PERMISSIONS.VIEW_CUSTOMER_MATERIAL],
  at: { params: { customerId: "c-beispiel" }, hash: "materials" },
})
const onPacket = stepsOn("PacketDetail", {
  permissions: [PERMISSIONS.VIEW_PACKET_GENERAL],
  at: { params: { packetId: "pkt-fundament" } },
})
const onProject = stepsOn("ProjectDetail", {
  permissions: [PERMISSIONS.VIEW_ASSIGNED_PROJECT_MATERIAL, PERMISSIONS.VIEW_FOREIGN_PROJECT_MATERIAL],
  at: { params: { projectId: "p-nord" }, hash: "materials" },
})
// The Stundenlohnarbeiten register of the same project — the register a material is actually booked
// in, gated by its own pair of rights rather than by the Material one.
const onProjectReports = stepsOn("ProjectDetail", {
  permissions: [PERMISSIONS.VIEW_ASSIGNED_PROJECT_HOURLYWAGEREPORT, PERMISSIONS.VIEW_FOREIGN_PROJECT_HOURLYWAGEREPORT],
  at: { params: { projectId: "p-nord" }, hash: "hourlyWageReports" },
})
// And where those booked quantities are priced. `nav-costs` is the drawer entry's own gate, the pair
// of cost rights the four cost routes are guarded by — one place decides who sees any of them.
const onCosts = stepsOn("ProjectCostsForOne", {
  navEntry: "nav-costs",
  at: { params: { projectId: "p-nord" } },
})

/**
 * Materials: the catalogue every project, packet and customer price list draws from, and the place the
 * purchase price each of them calculates with is kept.
 *
 * The list first; then Beton C25/30's page, whose registers the pointer walks through — the master data, the
 * purchase price (a group of Allgemein before the supplier setup, the Einkauf tab with its suppliers after
 * it), the note, the Dateiablage and the Protokoll — with Speichern
 * pointed at and never pressed. The Zusammenhänge chapter is five steps on the places outside the
 * catalogue where a material shows up, each naming what it does there — the Stundenlohnarbeit opened
 * on the Material register it is booked in.
 *
 * The twin of the machines tour (`tours/machines.ts`): the two features share their views, their
 * components and their wording, and a change to one of them nearly always belongs in the other.
 */
export const MATERIALS_TOUR: ViewHelpTour = {
  key: "materials",
  icon: ENTITY_ICONS.material,
  // The create form has no steps of its own; its trigger opens the tour at the top of the list.
  routes: ["MaterialOverview", "MaterialDetail", "MaterialCreate", "MaterialComparison"],
  // Where a material shows up outside the catalogue. Played last, after a rule on the timeline.
  related: ["CustomerDetail", "PacketDetail", "ProjectDetail", "ProjectCostsForOne"],
  scenes: [
    // The catalogue's one create button, opened to show what it offers — a record of the catalogue or a
    // packet of it — and closed again with Escape. Neither entry is pressed: both leave the list for a
    // create form.
    { key: "create-menu", enter: [{ do: "click", target: "catalog-create" }], exit: [{ do: "escape" }] },
    // The Allgemein register and its three groups. One scene, so a jump straight onto the note presses
    // the register first, at speed. No exit: the next scene presses its own register, and leaving the
    // page takes the open one with it.
    // The actions menu of the card's head, opened to show its entries and closed with Escape. Every
    // entry leaves the page or deletes, so none is pressed.
    { key: "actions", enter: [{ do: "click", target: "entity-actions" }], exit: [{ do: "escape" }] },
    { key: "general", enter: [], exit: [] },
    // The Einkauf, once the prices are suppliers'. The quantity typed for the delivered prices is taken out
    // again by the field's ✕, so going back into the tab does not find the column already there.
    { key: "purchase", enter: [], exit: [{ do: "clear", target: "material-purchase-quantity" }] },
    // The price history, folded until opened: its step opens it, the exit folds it again. The tab first, for a jump
    // straight onto the step.
    {
      key: "history",
      enter: [{ do: "openTab", target: "material-tab-purchase" }],
      exit: [{ do: "collapse", target: "material-purchase-history-toggle" }],
    },
    // The Dateiablage, and then the Protokoll. One press each, in a scene of its own, because a
    // scene's steps have to be consecutive.
    { key: "files", enter: [], exit: [] },
    { key: "activity", enter: [], exit: [] },
    // A customer's price register, and two of the project's, each pressed by the step that explains it.
    { key: "customer-materials", enter: [], exit: [] },
    { key: "project-materials", enter: [], exit: [] },
    // A report of the project opened from its register, left again by the form's own Abbrechen: the
    // drawer would otherwise stay open over the cost page's way back through the project.
    { key: "project-reports", enter: [], exit: [{ do: "click", target: "hwr-cancel" }] },
    // The Analyse view of the cost page, put back on the Heatmap the page opens on: the switch is a
    // toggle at the top of the page, not a tab of the register bar, and it survives a step's end.
    {
      key: "costs-analysis",
      enter: [{ do: "click", target: "costs-mode-analysis" }],
      exit: [{ do: "click", target: "costs-mode-heatmap" }],
    },
  ],
  steps: [
    // ── Übersicht ────────────────────────────────────────────────────────────
    onOverview("list", "material-table", ENTITY_ICONS.material, { placement: "top" }),
    onOverview("row", "row-mat-beton", "mdi-table-row", { placement: "bottom" }),
    // On the open menu of the one create button, which offers the material and the packet. Pointed at,
    // not pressed: pressing it would leave the list for the create form.
    onOverview("create", "material-create", "mdi-plus", {
      placement: "bottom",
      scene: "create-menu",
      permissions: EDIT,
    }),
    // The catalogue's packets are rows of this table too, and the filter above it narrows to either.
    // Pointed at, not pressed: the packets tour explains the packets.
    onOverview("kind-filter", "catalog-kind-filter", ENTITY_ICONS.packet, {
      placement: "bottom",
      permissions: [PERMISSIONS.VIEW_PACKET_GENERAL],
    }),

    // ── Beton C25/30 ─────────────────────────────────────────────────────────
    // The card's head is `LewEntityPage`'s, not this view's, which is why it carries its own anchor.
    // A catalogue record is named by what the head shows, so it comes before the registers.
    onDetail("head", "entity-head", "mdi-card-account-details-outline", { placement: "bottom" }),
    // Duplizieren and Paket daraus erstellen, pointed at on the open menu: both open a form.
    onDetail("action-duplicate", "entity-action-duplicate", "mdi-content-duplicate", {
      placement: "left",
      scene: "actions",
      permissions: EDIT,
    }),
    onDetail("action-packet", "entity-action-packet", ENTITY_ICONS.packet, {
      placement: "left",
      scene: "actions",
      permissions: [PERMISSIONS.EDIT_PACKET_GENERAL],
    }),
    onDetail("tabs", "material-tabs", "mdi-tab", { placement: "bottom" }),
    // The register is pressed with the whole page in shot: zoomed onto the tab, the panel that slides
    // in underneath would be out of the picture. The two groups after it are inside that panel.
    onDetail("base", "material-base", "mdi-form-textbox", {
      placement: "bottom",
      scene: "general",
      zoom: false,
      actions: [{ do: "openTab", target: "material-tab-general" }],
    }),
    // Before the setup only: afterwards the prices have a tab of their own, below.
    onDetail("purchase", "material-purchase", "mdi-cash", {
      placement: "bottom",
      scene: "general",
      permissions: VIEW_PRICE,
      when: legacy,
    }),
    onDetail("note", "material-note", "mdi-note-text-outline", { placement: "top", scene: "general" }),
    // The Einkauf: who sells the material, at what price, since when. Read here, changed at the supplier.
    onDetail("purchase-tab", "material-purchase", "mdi-truck-delivery-outline", {
      placement: "top",
      scene: "purchase",
      zoom: false,
      when: strict,
      actions: [{ do: "openTab", target: "material-tab-purchase" }],
    }),
    // Brought on screen whole: reached straight from the timeline, the table starts under the footer.
    onDetail("purchase-rows", "material-purchase-table", "mdi-table", {
      placement: "top",
      scene: "purchase",
      when: strict,
      actions: [{ do: "scroll", target: "material-purchase-table", to: "reveal" }],
    }),
    onDetail("purchase-default", "material-purchase-default", "mdi-star-outline", {
      placement: "right",
      scene: "purchase",
      when: strict,
    }),
    // The pencil beside the default's price: pointed at, not pressed — the dialog writes a price.
    onDetail("purchase-change", "material-purchase-change", "mdi-pencil-outline", {
      placement: "left",
      scene: "purchase",
      when: mayAddSupplier,
    }),
    // A quantity typed, with the whole table in view: the column it fills is the point.
    onDetail("purchase-quantity", "material-purchase-table", "mdi-calculator-variant-outline", {
      placement: "top",
      scene: "purchase",
      zoom: false,
      when: strict,
      permissions: VIEW_PRICE,
      actions: [{ do: "type", target: "material-purchase-quantity", text: "30" }],
    }),
    // Pointed at, not pressed: the dialog adds a row the moment it is confirmed.
    onDetail("purchase-add", "material-purchase-add", "mdi-plus", {
      placement: "left",
      scene: "purchase",
      when: mayAddSupplier,
    }),
    // Under Schmid's row, the project that buys the Beton from it at a price of its own.
    onDetail("purchase-object-prices", "material-purchase-object-prices-pr-beton-schmid", "mdi-tag-outline", {
      placement: "bottom",
      scene: "purchase",
      when: strict,
      permissions: VIEW_PRICE,
    }),
    onDetail("purchase-following", "following-bookings-hint", "mdi-calendar-sync-outline", {
      placement: "top",
      scene: "purchase",
      when: strict,
      permissions: REPRICE,
    }),
    // The add-on's chart, last of the Einkauf and in a scene of its own: folded on the tab, it is opened by its
    // head and folded again after, as the reader finds it. Its head says what the record costs in general.
    onDetail("purchase-history", "material-purchase-history", "mdi-chart-line", {
      placement: "bottom",
      scene: "history",
      when: strict,
      navEntry: "nav-supplier-insights",
      permissions: VIEW_PRICE,
      actions: [{ do: "expand", target: "material-purchase-history-toggle" }],
    }),
    onDetail("files", "material-files", "mdi-folder-outline", {
      placement: "top",
      scene: "files",
      zoom: false,
      actions: [{ do: "openTab", target: "material-tab-files" }],
    }),
    onDetail("activity", "material-activity", "mdi-history", {
      placement: "top",
      scene: "activity",
      zoom: false,
      actions: [{ do: "openTab", target: "material-tab-activity" }],
    }),
    // Explained, never pressed: it writes the record.
    onDetail("save", "material-save", "mdi-content-save-outline", { placement: "top", permissions: EDIT }),

    // ── Where to find this again ─────────────────────────────────────────────
    onDetail("again", "view-help-trigger", "mdi-help-circle-outline", { placement: "bottom" }),

    // ── Vergleichen (add-on) ────────────────────────────────────────────────
    onComparison("comparison", "material-comparison-cards", "mdi-compare-horizontal", { placement: "top" }),
    onComparison("comparison-pick", "material-comparison-card-1", "mdi-swap-horizontal", { placement: "bottom" }),
    onComparison("comparison-only", "material-comparison-only-0", "mdi-chevron-right", { placement: "top" }),

    // ── Zusammenhänge ──────────────────────────────────────────────────────────
    //
    // Not a tour of four more pages: what matters here is only where a material takes effect outside
    // the catalogue. So each step names the place and what the material does to it — and, where the
    // purchase price is what does the work, says which figure is computed from it.
    //
    // Both directions, in the order a material travels: priced for a customer, bundled in a packet,
    // carried by a project, booked on one of its Stundenlohnarbeiten, and finally valued in that
    // project's running post-calculation.
    onCustomer("customer-price", "customer-materials", ENTITY_ICONS.customer, {
      placement: "bottom",
      scene: "customer-materials",
      zoom: false,
      actions: [{ do: "openTab", target: "customer-tab-materials" }],
    }),
    onPacket("packet-items", "packet-items", ENTITY_ICONS.packet, { placement: "top" }),
    onProject("project-materials", "project-materials", ENTITY_ICONS.project, {
      placement: "top",
      scene: "project-materials",
      zoom: false,
      actions: [{ do: "openTab", target: "project-tab-materials" }],
    }),
    // Where a quantity is actually written down: the register is pressed, its oldest report opened —
    // hwr-nord-1, the first row, which books Beton — and the form turned to its Material register.
    // The register itself is the projects tour's to explain; this step shows only the place in one
    // report where a material is entered. No zoom: the camera would chase three presses across the
    // page and the drawer.
    onProjectReports("project-report", "hwr-materials", ENTITY_ICONS.hourlywagereport, {
      placement: "left",
      scene: "project-reports",
      zoom: false,
      actions: [
        { do: "openTab", target: "project-tab-hourlyWageReports" },
        { do: "click", target: "row-hwr-nord-1" },
        { do: "openTab", target: "tab-tabMaterials" },
      ],
    }),
    // And what those quantities become. The Analyse view is switched on by the scene, so the step
    // lands on the Kostenstruktur with the share of Material already drawn.
    onCosts("project-costs", "costs-analysis-structure", "mdi-chart-donut", {
      placement: "top",
      scene: "costs-analysis",
    }),
  ],
}
